import { computed, inject, Injectable, signal } from '@angular/core'
import { TitleCasePipe } from '@angular/common'
import { HttpClient, httpResource, HttpErrorResponse, HttpContext } from '@angular/common/http'
import { BehaviorSubject, lastValueFrom, Observable, of, take } from 'rxjs'
import { CommonService } from '@appShared/services/common.service'
import { PRESERVE_HTTP_ERROR } from '@appShared/services/http-request-interceptor'
import { setErrorMessage } from '@appShared/utils/error-message'
//import { AppFacade } from '@appShared/services/app.facade'
import {
   IAccount,
   IContact,
   IPaymentInstrument,
   IPaymentInstrumentSubmission,
   IProfile,
   IProfileSubmission,
   ISubscription,
   ISubscriptionAuthorization,
   IProgressBadge,
   ISubscriptionUpdateSubmission,
} from '@appShared/interfaces/[Model-based]'
import { IUser } from '@appShared/interfaces/[Interface-based]/user.interface'
import { PaymentInstrumentType_ } from '@appShared/services/lookup/[CodeGen]/payment-instrument-type.domain'
import { PaymentCardType_ } from '@appShared/services/lookup/[CodeGen]/payment-card-type.domain'
import { toSignal } from '@angular/core/rxjs-interop'

export interface AccountSearchCriteria {
   partnerRepSourceId: string
   subscriptionReferenceSourceId: string
}

export interface IClientPaymentInstrument extends IPaymentInstrument {
   paymentCardImageUrl: string
}

@Injectable({
   providedIn: 'root'
})
export class AccountsService {
   // services
   private _httpClient = inject(HttpClient)
   private _commonService = inject(CommonService)
   private _titleCasePipe = inject(TitleCasePipe)


   private _accountsApi = '/api/core/accounts'
   private _httpOptions = this._commonService.httpOptions()

   private _subscriptions: BehaviorSubject<
      ISubscription[]
   > = new BehaviorSubject<ISubscription[]>(null)
   public get subscriptions$(): Observable<ISubscription[]> {
      return this._subscriptions.asObservable()
   }

   private _subscriptionAuthorizations: BehaviorSubject<
      ISubscriptionAuthorization[]
   > = new BehaviorSubject<ISubscriptionAuthorization[]>(null)
   public get activeSubscriptionAuthorizations$(): Observable<ISubscriptionAuthorization[]> {
      return this._subscriptionAuthorizations.asObservable()
   }

   private _profiles: BehaviorSubject<
      IProfile[]
   > = new BehaviorSubject<IProfile[]>([])
   profiles = toSignal(this._profiles, { initialValue: [] })

   /*
   * private methods
   * */

   /*
   * public methods
   * */

   getCurrentUser(): Observable<IUser> {
      const url = `${this._accountsApi}/user`

      // callers (guards/facade) handle errors by status - keep the HttpErrorResponse
      return this._httpClient.get<IUser>(url, {
         context: new HttpContext().set(PRESERVE_HTTP_ERROR, true)
      })
   }

   /* Account Search */
   public accountSearchCriteria = signal<AccountSearchCriteria>({} as AccountSearchCriteria)
   // https://www.youtube.com/watch?v=ygAEZQnG1LI (debounce and other examples)
   public accountSearchResults = httpResource<IAccount[]>(() =>
      (this.accountSearchCriteria()?.partnerRepSourceId || this.accountSearchCriteria()?.subscriptionReferenceSourceId)
         ? `${this._accountsApi}/search?partnerRepSourceId=${this.accountSearchCriteria().partnerRepSourceId}&subscriptionReferenceSourceId=${this.accountSearchCriteria().subscriptionReferenceSourceId}`
         : undefined,
      {
         defaultValue: [] as IAccount[],
         parse: (response) => {
            /*TESTING for returning only 1 item in the result (also where you can massage the data) */
            //   const results = (response as ISubscription[]) ?? []
            //   return results.length ? [results[0]] : []

            return ((response as IAccount[]) ?? [])
               .filter(account => account.info.actionableSubscription != null)
         }
      }
   )
   public accountSearchIsLoading = computed(() => this.accountSearchResults.isLoading())
   public accountSearchError = computed(() => this.accountSearchResults.error() as HttpErrorResponse | undefined)
   public accountSearchErrorMessage = computed(() =>
      this.accountSearchError() ? setErrorMessage(this.accountSearchError(), 'account') : null
   )
   /** True once the user has entered at least one search field (i.e. a search
       has been requested). Lets the UI distinguish "no results" from
       "haven't searched yet." */
   public accountSearchHasCriteria = computed(() => {
      const criteria = this.accountSearchCriteria()
      return !!(criteria?.partnerRepSourceId || criteria?.subscriptionReferenceSourceId)
   })


   /*
   * public methods
   * */

   public clearSearch(): void {
      this.accountSearchCriteria.set({} as AccountSearchCriteria)
   }


   //subscriptionPaymentTransactionTransactions$(): Observable<ISubscriptionPaymentTransaction[]> {
   //   const url = `${this._accountsApi}/subscription-payment-transactions`

   //   return this._httpClient.get<ISubscriptionPaymentTransaction[]>(url)
   //      .pipe(
   //         map(subPayTrans => {
   //            subPayTrans.forEach(subPayTran => {
   //               subPayTran = this.getFormattedUpdatedPaymentInstrument(subPayTran.paymentTransaction)
   //            })

   //            return subPayTrans
   //         })
   //      )
   //}

   getFormattedUpdatedPaymentInstrument(paymentInstrumentParent: any): any {
      let paymentInstrument = this.returnClientPaymentInstrument(paymentInstrumentParent.paymentInstrument)
      let paymentCard = paymentInstrument.paymentCard
      paymentInstrumentParent.paymentInstrument = {
         ...paymentInstrumentParent.paymentInstrument,
         paymentCard,
         paymentCardImageUrl: paymentInstrument.paymentCardImageUrl,
         sourceName: paymentInstrument.sourceName
      } as any /* any to account for expando property paymentCardImageUrl */

      return paymentInstrumentParent
   }

   getNewProfile(): IProfile {
      return {
         id: 0,
         contact: {
            info: {}
         } as IContact,
         info: {}
      } as IProfile
   }

   getSubscriptions() {
      const url = `${this._accountsApi}/subscriptions`

      this._httpClient.get<ISubscription[]>(url).pipe(
         take(1)
      ).subscribe(subscriptions => {
         this._subscriptions.next(subscriptions)
      })
   }

   getActiveSubscriptionAuthorizations() {
      const url = `${this._accountsApi}/subscription-authorizations`

      this._httpClient.get<ISubscriptionAuthorization[]>(url).pipe(
         take(1)
      ).subscribe(subscriptionAuthorizations => {
         subscriptionAuthorizations.forEach(subscription => {
            subscription = this.getFormattedUpdatedPaymentInstrument(subscription)
         })
         this._subscriptionAuthorizations.next([...subscriptionAuthorizations])
      })
   }

   getProfiles(includeAccountOwner?: boolean, currentUser?: IUser) {
      const url = `${this._accountsApi}/profiles`

      //TODO - testing
      //let subscriptionSeats = [
      //   {
      //      id: 1234,
      //      profileId: 1234,
      //      profile: {
      //         contact: {
      //            id: 1234,
      //            firstName: 'Bubba',
      //            lastName: 'Johnson',
      //            emailAddress: 'bubba@johnson.com'
      //         } as IContact
      //      } as IProfile
      //   } as ISubscriptionSeat,
      //   {
      //      id: 2345,
      //      profileId: 2345,
      //      profile: {
      //         contact: {
      //            id: 2345,
      //            firstName: 'Jackie',
      //            lastName: 'Chan',
      //            emailAddress: 'jackie@chan.com'
      //         } as IContact
      //      } as IProfile
      //   } as ISubscriptionSeat,
      //   {
      //      id: 3456,
      //      profileId: 3456,
      //      profile: {
      //         contact: {
      //            id: 3456,
      //            firstName: 'Jojo',
      //            lastName: 'Smithersonstein',
      //            emailAddress: 'jojo@smithersonstein.com'
      //         } as IContact
      //      } as IProfile
      //   } as ISubscriptionSeat,
      //] as ISubscriptionSeat[]
      //this._subscriptionSeats.next(subscriptionSeats)


      /**
       * TODO - review for removal of nested subscription
       *    https://medium.com/@kateb.fedy/angular-best-practices-to-adapt-4c7687b16c85
       *    11. Avoid having Subscriptions Inside Subscriptions
       * */
      this._httpClient.get<IProfile[]>(url).pipe(
         take(1)
      ).subscribe(profiles => {
         if (!includeAccountOwner && currentUser) {

            const currentProfileId = (currentUser.profile as IProfile).id
            profiles = [...profiles].filter(profile => profile.id !== currentProfileId)
            this._profiles.next(profiles)

         } else {
            this._profiles.next(profiles)
         }
      })
   }

   getProfileBadges(profileId: number): Observable<IProgressBadge[]> {

      if (profileId) {
         const url = `${this._accountsApi}/profiles/${profileId}/badges`

         return this._httpClient.get<IProgressBadge[]>(url)
      }

      return of([])
   }

   returnClientPaymentInstrument(paymentInstrument: IPaymentInstrument) {
      let clientPaymentInstrument = {
         ...paymentInstrument
      } as IClientPaymentInstrument

      if (
         clientPaymentInstrument.typeCode === PaymentInstrumentType_.PaymentCard
      ) {
         const ccExpiration = clientPaymentInstrument.paymentCard.expiration

         if (ccExpiration?.length > 5) {
            clientPaymentInstrument.paymentCard.expiration = ccExpiration
               ? this._commonService.dateTime.formatDate(
                  new Date(ccExpiration),
                  'MM/YY'
               )
               : ''
         }

         if (
            [
               PaymentCardType_.Amex,
               PaymentCardType_.Discover,
               PaymentCardType_.MasterCard,
               PaymentCardType_.VISA
            ].includes(clientPaymentInstrument?.info.card?.typeCode || 0)
         ) {
            clientPaymentInstrument.paymentCardImageUrl = clientPaymentInstrument.sourceName
               ? `/assets/images/cc/${clientPaymentInstrument.sourceName.toLowerCase()}.png`
               : ''
         }

      } else if (
         clientPaymentInstrument.typeCode === PaymentInstrumentType_.EftRecord
      ) {
         /* Title case EFT Bank Names */
         let sourceName = this._titleCasePipe.transform(
            clientPaymentInstrument.sourceName
         )
         clientPaymentInstrument.sourceName = sourceName

         //don't show expiration in case of EFT
         //clientPaymentInstrument.expiration = ''
      }

      return clientPaymentInstrument
   }

   async createUpdateProfile(profileSubmission: IProfileSubmission, profileId?: number): Promise<any> {

      if (!profileSubmission) {
         return Promise.reject('No profileSubmission passed!')
      }

      let url = `${this._accountsApi}/profiles`
      const options = this._commonService.httpOptions()
      let request$: Observable<IProfile>

      if (profileId) {
         /* update */
         url = `${url}/${profileId}`
         request$ = this._httpClient.put<IProfile>(url, profileSubmission, options).pipe(take(1))
      } else {
         /* else create */
         request$ = this._httpClient.post<IProfile>(url, profileSubmission, options).pipe(take(1))
      }

      return await lastValueFrom<IProfile>(request$)
   }

   async deleteProfile(
      profileId: number
   ): Promise<any> {
      if (!profileId) {
         return Promise.reject('profileId NOT passed!')
      }

      const url = `${this._accountsApi}/profiles/${profileId}`

      const request$ = this._httpClient.delete(url, this._httpOptions).pipe(take(1))

      return await lastValueFrom(request$)
   }

   async updateSubscription(
      subscriptionUpdateSubmission: ISubscriptionUpdateSubmission
   ): Promise<ISubscription> {
      if (!subscriptionUpdateSubmission?.subscriptionId) {
         return Promise.reject('Not all subscriptionUpdateSubmission passed!')
      }

      const url = `${this._accountsApi}/subscriptions/${subscriptionUpdateSubmission.subscriptionId}`

      const request$ = this._httpClient.put<ISubscription>(url, subscriptionUpdateSubmission, this._httpOptions).pipe(take(1))

      return await lastValueFrom<ISubscription>(request$)
   }

   async submitPaymentTransactionReversal(
      accountSafeId: string,
      subscriptionPaymentTransactionSafeId: string
   ): Promise<IAccount> {
      if (!accountSafeId || !subscriptionPaymentTransactionSafeId) {
         return Promise.reject('Not all accountSafeId/subscriptionPaymentTransactionSafeId passed!')
      }

      // safeIds are encrypted base64, so each must be encoded before the path segment
      const url = `${this._accountsApi}/${encodeURIComponent(accountSafeId)}/subscription-payment-transactions/${encodeURIComponent(subscriptionPaymentTransactionSafeId)}/submit-reversal`

      const request$ = this._httpClient.put<IAccount>(url, null, this._httpOptions).pipe(take(1))

      return await lastValueFrom<IAccount>(request$)
   }

   async updateSubscriptionAuthorizations(
      accountSafeId: string,
      paymentInstrumentSubmission: IPaymentInstrumentSubmission
   ): Promise<IAccount> {
      if (!paymentInstrumentSubmission) {
         return Promise.reject('No paymentInstrumentSubmission passed!')
      }

      const url = `${this._accountsApi}/${encodeURIComponent(accountSafeId)}/subscription-authorizations`

      const request$ = this._httpClient.put<IAccount>(url, paymentInstrumentSubmission, this._httpOptions).pipe(take(1))

      return await lastValueFrom<IAccount>(request$)
   }

   async setSubscriptionConversionOptionPlanned(
      accountSafeId: string,
      subscriptionSafeId: string,
      subscriptionConversionOptionSafeId: string
   ): Promise<ISubscription> {
      if (!accountSafeId || !subscriptionSafeId || !subscriptionConversionOptionSafeId) {
         return Promise.reject('Not all accountSafeId/subscriptionSafeId/subscriptionConversionOptionSafeId passed!')
      }

      // safeIds are encrypted base64, so each must be encoded before its path segment
      const url = `${this._accountsApi}/${encodeURIComponent(accountSafeId)}/subscriptions/${encodeURIComponent(subscriptionSafeId)}/subscription-conversion-options/${encodeURIComponent(subscriptionConversionOptionSafeId)}/set-planned`

      const request$ = this._httpClient.put<ISubscription>(url, null, this._httpOptions).pipe(take(1))

      return await lastValueFrom<ISubscription>(request$)
   }

   async updateSubscriptionSeatLimit(
      accountSafeId: string,
      subscriptionSafeId: string,
      seatLimit: number
   ): Promise<IAccount> {
      if (!accountSafeId || !subscriptionSafeId || !seatLimit) {
         return Promise.reject('Not all accountSafeId/subscriptionSafeId/seatLimit passed!')
      }

      // safeIds are encrypted base64, so each must be encoded before its path segment
      const url = `${this._accountsApi}/${encodeURIComponent(accountSafeId)}/subscriptions/${encodeURIComponent(subscriptionSafeId)}/seat-limit`

      const request$ = this._httpClient.put<IAccount>(url, seatLimit, this._httpOptions).pipe(take(1))

      return await lastValueFrom<IAccount>(request$)
   }
}
